'use client';

import React, {createContext, ReactNode, useContext, useCallback} from 'react';

/**
 * Lightweight provider for discountCodeProvider function
 * Must be positioned ABOVE CoursePriceProvider in the tree
 *
 * This provider computes the discountCodeProvider function that tells
 * the pricing API which discount code to validate.
 *
 * Provider Hierarchy:
 * DiscountCodeProvider (this)
 *   → CoursePriceProvider (receives discountCodeProvider)
 *     → CLPPurchaseContextProvider (uses useCoursePrice)
 */
interface DiscountCodeProviderContextValue {
    discountCodeProvider: () => string | undefined;
}

const DiscountCodeProviderContext = createContext<DiscountCodeProviderContextValue | undefined>(
    undefined,
);

export function DiscountCodeProvider({children}: {children: ReactNode}) {
    /**
     * Discount code provider for CoursePriceProvider
     *
     * CRITICAL ARCHITECTURE NOTE:
     * ===========================
     * The pricing API (/api-2.0/pricing/) only accepts ONE couponCode parameter per request,
     * which applies to ALL courses in that batch.
     *
     * For CLP, we ONLY use the URL as the source of truth for discount codes:
     *
     * Why NOT use couponStore.currentCodes?
     * - couponStore.currentCodes comes from the CART API (discount_attempts)
     * - It's CART-WIDE (contains codes for all courses in cart)
     * - If cart has Course A with "SAVE10" and Course B with "SPRING20", we can't determine
     *   which code applies to THIS course from currentCodes[0]
     * - The Cart API handles course-specific code mapping server-side, but the pricing API doesn't
     *
     * Solution:
     * - Non-cart: URL is the single source of truth (?couponCode=XXX)
     * - Cart: Don't pass a code to pricing API - let it fetch from cart session server-side
     * - Manual entry: Optimistically added to URL, then validated by pricing API reload
     *
     * Flow:
     * 1. Page loads with ?couponCode=XXX → read from URL
     * 2. User manually enters code → added to URL, pricing reloads
     * 3. Pricing API validates with code from URL
     * 4. Valid: stays in URL, Invalid: removed from URL
     */
    const discountCodeProvider = useCallback(() => {
        // Always read from URL - it's the single source of truth for CLP
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('couponCode') || undefined;
        }

        return undefined;
    }, []);

    return (
        <DiscountCodeProviderContext.Provider value={{discountCodeProvider}}>
            {children}
        </DiscountCodeProviderContext.Provider>
    );
}

/**
 * Hook to access the discount code provider function
 * @returns DiscountCodeProviderContextValue
 * @throws Error if used outside of DiscountCodeProvider
 */
export function useDiscountCodeProvider(): DiscountCodeProviderContextValue {
    const context = useContext(DiscountCodeProviderContext);
    if (!context) {
        throw new Error('useDiscountCodeProvider must be used within DiscountCodeProvider');
    }
    return context;
}
